import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  BlockFoot,
  BlockHead,
  BlockSummary,
  type BlockTrialSlot,
} from '@/components/admin/sessions/walk/walk-block-parts';
import { FoldSummary, FoldToggle, useFold } from '@/components/admin/sessions/walk/walk-fold';
import { RangementTable } from '@/components/admin/sessions/walk/walk-rangement-table';
import { fetchRangerPrompts } from '@/lib/session-api';
import { costLabel } from '@/lib/session-labels';
import { attemptsWord, secondsLabel } from '@/lib/session-noyau';
import {
  RANGEMENT_FAILED_COLUMN_NOTE,
  RANGEMENT_FAILED_NOTE,
  RANGEMENT_OBJECTIVE_MARK,
  RANGEMENT_OBJECTIVE_STATE_LABEL,
  RANGEMENT_STATE_LABEL,
  type RangementBlockState,
  duplicateExtractsLabel,
  playedTargetIds,
  rangementAttemptCause,
  rangementDurationMs,
  rangementEditorSearch,
  rangementFingerprint,
  rangementModel,
  rangementObjectiveTone,
  rangementRowIds,
  rangementSentence,
  rangementSummary,
  rangementTargetOf,
  rangementTone,
  removedExtractsLabel,
  turnPlace,
} from '@/lib/session-rangement';
import { TOC_ANCHORS } from '@/lib/session-toc';
import { cn } from '@/lib/utils';
import {
  RANGEMENT_DISCARD_REASON_LABELS,
  type RangementObjective,
  type RangementTrial,
  type RangerPrompt,
  type SessionDetail,
  type SessionRangement,
} from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * The block « Rangement de la matière » of the parcours (scenario-succes.md §7,
 * issue #177; maquettes `Main` and `RangementEtats`) - under the protocol's,
 * before the determination's it feeds, bordered like an ordinary block.
 *
 * ## It arrives FOLDED, on one line (#219)
 *
 * As the determination's (`walk-noyau-block.tsx`, `walk-fold.tsx`): the state stays in the header,
 * the line says what the rendering came to - « 5 portées, 1 imprécise, 6 écartés » - the model, the
 * time and the cost, and « Voir la matière ▾ » draws what the block always drew. A state with no
 * rendering says its sentence in the line. The block of a trial of the ranger folds the same way.
 *
 * ## What it says
 *
 * The state - rendu, en échec, aucun, sans matière, en cours -, the seat, the
 * model the API answered, the time from the launch to the settlement, the cost,
 * the attempts, the passages the verbatim check removed and, since #187, the
 * passages the setting « un passage ne sert qu'un objectif » kept in one
 * objective only (« 1 passage rangé deux fois : gardé en 1. Tension vitale,
 * retiré de 6. Sens profond », beside the first, relecture.md §12); the consigne the
 * ranger was served - the version of the protocol, a link to ITS Rangement view
 * - and its fingerprint. Then the table of three columns (`RangementTable`),
 * the passages the ranger read and put nowhere with their reason, and « Voir
 * l'entrée servie au rangeur ».
 *
 * ## Mounted only where there is something to say
 *
 * The route mounts it when the session HAS a rangement, or when the protocol it
 * played carries objective targets - and then a missing rangement is « aucun »:
 * a session closed by a guardrail, an expired one, or one played before the
 * ranger existed. A session whose protocol had no target gets no block, and its
 * only door to the ranger is the blank rangement below.
 *
 * ## It never recounts
 *
 * The cost of the ranger is in the tiles of the head already; the euro printed
 * here is the rangement's own, the same column.
 *
 * ## The served entry is read on a CLICK (§9)
 *
 * What the ranger was sent carries the person's words a second time: it is
 * behind its own route, mounted only when somebody asks - the régime of the
 * analyst's.
 *
 * ## The same block for a TRIAL of the ranger (§7.3)
 *
 * A trial fills the column of the ranger and says « à blanc, vN ». It draws its
 * own block, under its own marks, because the session may have no block of its
 * own to fill; the scribe's column is the session's, and the session's
 * rangement does not move. A trial keeps its served entry with it.
 */

export interface WalkRangementBlockProps {
  readonly detail: SessionDetail;
  /** A trial of the ranger drawn by this block in place of the session's own rangement. */
  readonly trial?: RangementTrial | undefined;
  /**
   * Unfold the block when this RISES (or is up at mount) - the `unfold` of `walk-noyau-block.tsx`.
   * No pill of the head leads to a rangement yet; the prop is the seam, as the noyau's is.
   */
  readonly unfold?: boolean;
  /**
   * The rank of the protocol page the block hangs on (#224): its header says « Bloc de la page 5 ».
   * Null on a block that hangs on no visit. The block of a TRIAL has no such header.
   */
  readonly rank?: number | null;
  /**
   * The foot's slot (#224): the route's dry trial - or the vitrine's note - drawn in the foot, and
   * what stands under it. Absent, the foot says its sentence of origin and nothing else. Never
   * read for a TRIAL.
   */
  readonly foot?: BlockTrialSlot | undefined;
}

/** The figure a block draws: the session's rangement, or a trial's - the two share their shape. */
type Figure = SessionRangement | RangementTrial;

/** « Rangée en fond, à la clôture du protocole » (§3.3) - how the block came, what its foot says. */
const RANGEMENT_ORIGIN = 'Rangée en fond, à la clôture du protocole';

export function WalkRangementBlock({
  detail,
  trial,
  unfold = false,
  rank = null,
  foot,
}: WalkRangementBlockProps) {
  const fold = useFold(unfold);
  const [served, setServed] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const figure: Figure | null = trial ?? detail.rangement;
  const state: RangementBlockState = figure === null ? 'none' : figure.state;
  const protocol = detail.cascade.protocol;
  const targets = playedTargetIds(detail).length;
  const sentence = rangementSentence(state, {
    protocol: protocol === null ? null : `${protocol.name} v${protocol.versionNumber}`,
    targets: trial === undefined ? targets : (trial.objectives?.length ?? targets),
    trial: trial !== undefined,
  });

  const calls = figure?.calls ?? [];
  const ids =
    trial === undefined
      ? rangementRowIds(detail, detail.rangement?.objectives ?? null)
      : (trial.objectives ?? []).map((objective) => objective.objectiveId);
  const objectives = figure?.objectives ?? null;
  const discarded = figure?.discarded ?? [];
  const failed = state === 'failed';

  return (
    <article
      // The anchor of the sommaire (#221): the block of the session only, a trial's is not in it.
      id={trial === undefined ? TOC_ANCHORS.rangement : undefined}
      data-testid={
        trial === undefined
          ? SESSIONS_TEST_IDS.walkRangement
          : SESSIONS_TEST_IDS.rangementTrialBlock
      }
      data-state={state}
      data-open={fold.open ? 'true' : 'false'}
      className={cn(
        'overflow-hidden rounded-md border border-gray-200 bg-white',
        // The block of the session hangs under the protocol card (#224): indented, like the maquette.
        trial === undefined ? 'ml-3 sm:ml-7' : null,
      )}
    >
      {trial === undefined ? (
        <BlockHead
          rank={rank}
          title="Rangement de la matière"
          fold={fold}
          closedLabel="Voir la matière ▾"
          toggleTestId={SESSIONS_TEST_IDS.walkRangementToggle}
          pills={
            <span data-testid={SESSIONS_TEST_IDS.walkRangementState} data-state={state}>
              <StatusBadge tone={rangementTone(state)}>{RANGEMENT_STATE_LABEL[state]}</StatusBadge>
            </span>
          }
          summary={
            fold.open ? null : (
              <BlockSummary
                testId={SESSIONS_TEST_IDS.walkRangementSummary}
                outcome={
                  <RangementOutcome
                    figure={figure}
                    state={state}
                    sentence={sentence}
                    trial={false}
                  />
                }
                facts={figure === null ? null : <FigureSpans figure={figure} />}
              />
            )
          }
        />
      ) : (
        <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-gray-100 border-b bg-gray-50 px-4 py-2.5">
          <span className="font-bold text-gray-500 text-micro uppercase tracking-wide">Essai</span>
          <h3 className="font-bold text-gray-900 text-small">Rangement de l’essai</h3>
          <span
            data-testid={SESSIONS_TEST_IDS.walkRangementState}
            data-state={state}
            className="ml-auto"
          >
            <StatusBadge tone={rangementTone(state)}>{RANGEMENT_STATE_LABEL[state]}</StatusBadge>
          </span>
          <FoldToggle
            fold={fold}
            closedLabel="Voir la matière ▾"
            testId={SESSIONS_TEST_IDS.rangementTrialToggle}
          />
        </header>
      )}

      {fold.open ? null : trial === undefined ? (
        // One pill per objective target: what the ranger made of each, readable shut.
        state === 'rendered' && objectives !== null ? (
          <ObjectivePills detail={detail} ids={ids} objectives={objectives} />
        ) : null
      ) : (
        <FoldSummary testId={SESSIONS_TEST_IDS.rangementTrialSummary}>
          <RangementOutcome figure={figure} state={state} sentence={sentence} trial />
          {figure === null ? null : <FigureSpans figure={figure} />}
        </FoldSummary>
      )}

      {fold.open ? (
        <div
          id={fold.bodyId}
          className={cn(
            'flex flex-col gap-3.5 px-4 py-3',
            trial === undefined ? 'border-gray-100 border-t' : null,
          )}
        >
          {figure === null ? null : (
            <Facts
              figure={figure}
              detail={detail}
              trial={trial !== undefined}
              duplicates={duplicateExtractsLabel(
                figure.duplicateExtracts,
                ids,
                (objectiveId) => rangementTargetOf(detail, objectiveId).name,
              )}
            />
          )}

          {sentence === null ? null : <p className="text-caption text-gray-700">{sentence}</p>}

          {failed ? (
            <>
              <Attempts figure={figure} />
              <Alert tone="danger">
                {trial === undefined
                  ? RANGEMENT_FAILED_NOTE
                  : 'La détermination de la session n’a pas bougé : cet essai n’a rien rendu.'}
              </Alert>
              {trial === undefined ? (
                <p className="text-caption text-gray-600">{RANGEMENT_FAILED_COLUMN_NOTE}</p>
              ) : null}
            </>
          ) : null}

          {ids.length === 0 ? null : (
            <RangementTable
              detail={detail}
              ids={ids}
              objectives={objectives}
              trialVersion={trial === undefined ? null : trial.protocolVersion.version}
            />
          )}

          {state === 'rendered' ? (
            <section
              data-testid={SESSIONS_TEST_IDS.walkRangementDiscarded}
              aria-labelledby={
                trial === undefined ? 'rangement-ecartes' : 'rangement-trial-ecartes'
              }
              className="flex flex-col gap-2"
            >
              <h4
                id={trial === undefined ? 'rangement-ecartes' : 'rangement-trial-ecartes'}
                className="font-semibold text-gray-600 text-caption uppercase leading-4 tracking-wide"
              >
                Écartés par le rangeur
              </h4>
              {discarded.length === 0 ? (
                <p className="text-caption text-gray-600">
                  Aucun passage lu et mis nulle part : tout ce que le rangeur n’a pas rangé est
                  simplement non rangé.
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {discarded.map((passage, index) => (
                    <li
                      // By position: one sentence may be discarded twice, on two turns.
                      key={`${passage.turn}-${index}`}
                      data-testid={SESSIONS_TEST_IDS.walkRangementDiscardedRow}
                      data-reason={passage.reason}
                      data-turn={passage.turn}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-caption text-gray-700 leading-5"
                    >
                      <span className="w-24 shrink-0 text-gray-600 text-caption">
                        {turnPlace(detail, passage.turn)}
                      </span>
                      <span className="min-w-0 flex-1">« {passage.text} »</span>
                      <StatusBadge tone="neutral">
                        {RANGEMENT_DISCARD_REASON_LABELS[passage.reason]}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {failed && calls.some((call) => call.output !== '') ? (
            <div className="flex flex-col gap-2">
              <p className="text-caption">
                <button
                  type="button"
                  aria-expanded={showOutput}
                  onClick={() => setShowOutput((open) => !open)}
                  className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                >
                  {showOutput ? 'Masquer la sortie illisible' : 'Voir la sortie illisible'}
                </button>
              </p>
              {showOutput
                ? calls.map((call, index) =>
                    call.output === '' ? null : (
                      <pre
                        // By position: a rangement is a call and its retry, in order.
                        key={`${call.startedAt}-${index}`}
                        className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-gray-50 p-2.5 font-mono text-gray-900 text-caption leading-5"
                      >
                        {call.output}
                      </pre>
                    ),
                  )
                : null}
            </div>
          ) : null}

          {calls.length === 0 ? null : served ? (
            trial === undefined ? (
              <RangerServedPrompts sessionId={detail.session.id} />
            ) : (
              <RangerPromptsList prompts={trial.prompts} />
            )
          ) : (
            <p className="flex flex-wrap items-center gap-3 text-caption">
              <button
                type="button"
                data-testid={SESSIONS_TEST_IDS.walkRangementPrompts}
                onClick={() => setServed(true)}
                className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
              >
                Voir l’entrée servie au rangeur
              </button>
              <span className="text-gray-500 text-caption">
                {trial === undefined ? 'chargée à la demande' : 'gardée avec l’essai'}
              </span>
            </p>
          )}
        </div>
      ) : null}

      {trial === undefined
        ? (foot?.({ origin: RANGEMENT_ORIGIN }) ?? <BlockFoot origin={RANGEMENT_ORIGIN} />)
        : null}
    </article>
  );
}

/**
 * The pills of a folded rangement (#224, maquette `Main`): one per objective target, in the order
 * of the table - « ✓ Tension vitale » green when the person's words carry it, « ! Savoir-faire
 * naturel » yellow when they brush it, a hyphen on grey when nothing does. Read from the rangement
 * the session already carries; a target the ranger gave no state to is not drawn.
 */
function ObjectivePills({
  detail,
  ids,
  objectives,
}: {
  detail: SessionDetail;
  ids: readonly string[];
  objectives: readonly RangementObjective[];
}) {
  const pills = ids.flatMap((objectiveId) => {
    const objective = objectives.find((one) => one.objectiveId === objectiveId);
    return objective === undefined
      ? []
      : [{ objective, name: rangementTargetOf(detail, objectiveId).name }];
  });
  if (pills.length === 0) return null;
  return (
    <ul
      data-testid={SESSIONS_TEST_IDS.walkRangementObjectives}
      aria-label="Ce que le rangeur a fait de chaque objectif"
      className="flex flex-wrap gap-1.5 px-4 pb-3"
    >
      {pills.map(({ objective, name }) => (
        <li
          key={objective.objectiveId}
          data-testid={SESSIONS_TEST_IDS.walkRangementObjective}
          data-objective={objective.objectiveId}
          data-state={objective.state}
        >
          <StatusBadge tone={rangementObjectiveTone(objective.state)} size="xs">
            <span aria-hidden="true">{RANGEMENT_OBJECTIVE_MARK[objective.state]}</span>{' '}
            <span className="sr-only">{RANGEMENT_OBJECTIVE_STATE_LABEL[objective.state]} : </span>
            {name}
          </StatusBadge>
        </li>
      ))}
    </ul>
  );
}

/**
 * What a FOLDED block says first (#219): what a rendering came to - « 5 portées, 1 imprécise, 6
 * écartés », the very words of the list of trials - and, on every other state, the sentence the
 * open block says under its header (a failure of the session says its note, which the open block
 * draws in a callout): what asks for attention is never behind the fold.
 */
function RangementOutcome({
  figure,
  state,
  sentence,
  trial,
}: {
  figure: Figure | null;
  state: RangementBlockState;
  sentence: string | null;
  trial: boolean;
}) {
  if (state === 'rendered' && figure !== null) {
    return (
      <span>
        <b className="font-semibold text-gray-900">
          {rangementSummary(figure.objectives ?? [], figure.discarded ?? [])}
        </b>
      </span>
    );
  }
  const text = state === 'failed' && !trial ? RANGEMENT_FAILED_NOTE : sentence;
  return text === null ? null : <span className="min-w-0 basis-full">{text}</span>;
}

/**
 * The line of facts under the head (§7.1): the seat, the model, the time, the
 * cost, the attempts, then the consigne the ranger was served and its fingerprint.
 */
function Facts({
  figure,
  detail,
  trial,
  duplicates,
}: {
  figure: Figure;
  detail: SessionDetail;
  trial: boolean;
  /** « 1 passage rangé deux fois : … » (relecture.md §12), null when the setting removed none. */
  duplicates: string | null;
}) {
  const fingerprint = rangementFingerprint(figure);
  const removed = removedExtractsLabel(figure.removedExtracts);
  const protocol = detail.cascade.protocol;
  const { version, versionId } = figure.protocolVersion;

  return (
    <p className="flex flex-wrap gap-x-5 gap-y-2 text-caption text-gray-600">
      <span>
        Siège <b className="font-semibold text-gray-900">Rangeur</b>
      </span>
      <FigureSpans figure={figure} />
      {removed === null ? null : <span>{removed}</span>}
      {duplicates === null ? null : (
        <span data-testid={SESSIONS_TEST_IDS.walkRangementDuplicates}>{duplicates}</span>
      )}
      <span>
        Consigne{trial ? ' de développement' : ''}{' '}
        {protocol === null ? (
          <b className="font-semibold text-gray-900">v{version}</b>
        ) : (
          <Link
            to="/admin/protocoles/$protocolId"
            params={{ protocolId: protocol.id }}
            search={rangementEditorSearch(versionId)}
            className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            {protocol.name} v{version}
          </Link>
        )}
        {fingerprint === null ? null : (
          <>
            {' '}
            · empreinte <code className="font-mono text-gray-900 text-caption">{fingerprint}</code>
          </>
        )}
      </span>
    </p>
  );
}

/** The model, the time, the cost and the attempts - what the header's facts and a folded summary both say. */
function FigureSpans({ figure }: { figure: Figure }) {
  const model = rangementModel(figure);
  const duration = rangementDurationMs(figure);
  return (
    <>
      {model === null ? null : (
        <span>
          Modèle <b className="font-semibold text-gray-900">{model}</b>
        </span>
      )}
      {figure.calls.length === 0 ? (
        <span>{figure.state === 'pending' ? 'Appel en cours' : 'Aucun appel'}</span>
      ) : (
        <>
          {duration === null ? null : (
            <span>
              <b className="font-bold text-gray-900 tabular-nums">{secondsLabel(duration)}</b>
            </span>
          )}
          <span>
            <b className="font-bold text-gray-900 tabular-nums">{costLabel(figure.costEur)}</b>
          </span>
          <span>
            <b className="font-bold text-gray-900 tabular-nums">{figure.calls.length}</b>{' '}
            {attemptsWord(figure.calls.length)}
          </span>
        </>
      )}
    </>
  );
}

/** « Essai 2 · modèle · cause » - what each attempt of a failed ranger came to (§7.2). */
function Attempts({ figure }: { figure: Figure | null }) {
  const calls = figure?.calls ?? [];
  if (calls.length === 0) return null;
  return (
    <ol className="flex flex-col gap-1.5">
      {calls.map((call, index) => (
        <li
          // By position: a rangement is a call and its retry, in order.
          key={`${call.startedAt}-${index}`}
          className="text-caption text-gray-700"
        >
          <b className="font-semibold text-gray-900">Essai {index + 1}</b>
          {call.model === null ? '' : ` · ${call.model}`} ·{' '}
          <span className="text-jaune-deep">
            {rangementAttemptCause(call) ?? 'Rangement rendu.'}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * What the ranger was served, call by call, read when this component MOUNTS -
 * the régime of the served inputs of a turn and of the analyst.
 */
function RangerServedPrompts({ sessionId }: { sessionId: string }) {
  const served = useQuery({
    queryKey: ['session-rangement-prompts', sessionId],
    queryFn: () => fetchRangerPrompts(sessionId),
    // Written once with the settlement (§9): a re-read could never answer anything new.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (served.isPending) {
    return <p className="text-caption text-gray-600">Lecture de l’entrée servie…</p>;
  }
  if (served.isError || served.data === undefined) {
    return (
      <p role="alert" className="text-caption text-rouge">
        L’entrée servie n’a pas pu être lue. Elle est purgée avec la session.
      </p>
    );
  }
  return <RangerPromptsList prompts={served.data.prompts} />;
}

/** What the ranger was served, call by call - read from the base, or held with a trial. */
function RangerPromptsList({ prompts }: { prompts: readonly RangerPrompt[] }) {
  if (prompts.length === 0) {
    return <p className="text-caption text-gray-600">Aucune entrée servie n’a été conservée.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {prompts.map((prompt, index) => (
        <section
          // By position: a rangement is a call and its retry, in order.
          key={`${prompt.seat}-${index}`}
          className="flex flex-col gap-1.5"
        >
          <h4 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
            Entrée servie · essai {index + 1}
          </h4>
          <pre className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900 text-caption leading-5">
            {prompt.system}
          </pre>
          {prompt.messages.map((message, rank) => (
            <pre
              key={`${message.role}-${rank}`}
              className="max-h-65 overflow-auto rounded-md border border-gray-200 bg-white p-3 font-mono text-gray-900 text-caption leading-5"
            >
              {message.role === 'user' ? '[user] ' : '[assistant] '}
              {message.content}
            </pre>
          ))}
        </section>
      ))}
    </div>
  );
}
